/**
 * Feature E02-F01 — "Список проектов (Dashboard)" request/response contracts.
 *
 * 1:1 port of the legacy n8n `get_projects` / `create_manual` flows
 * (docs/source-n8n/reference/Project.md, _common.md + sql-queries.md
 * "DB: Get Projects", "DB: Validate Session1"). The old SPA POSTed
 * `{ command:'get_projects', userId, role, session_token }` to one webhook and
 * rendered the returned ARRAY in `dashboard.js → renderProjectList`. The v2
 * transport is clean REST under `/projects` but the SAME logical contract — the
 * project portfolio list + the empty-project create.
 *
 * Endpoints honoured by the feature route (`routes/projects-dashboard.ts`):
 *   GET  /projects        → ProjectListItemDTO[]        (get_projects)
 *
 * The create-empty-project endpoint (`create_manual`) is owned by the project
 * mutations feature (E02-F03, `routes` for `/projects` POST + `services/
 * projectMutations.ts → createManualProject`); the Dashboard "New Project"
 * button (E02-F01-T06) calls it and validates the reply with
 * {@link createManualProjectResponseSchema} declared here so the Dashboard's
 * own contract for that interaction is pinned in this feature file.
 *
 * Parity notes carried over from the legacy slice:
 *   - get_projects returns a BARE ARRAY (not an object). `dashboard.js`
 *     explicitly does `if (Array.isArray(response))` — the wire MUST stay an
 *     array, hence {@link projectListResponseSchema} = `z.array(...)`.
 *   - Each row carries `client_name` (the legacy `SELECT p.*, c.name AS
 *     client_name … LEFT JOIN clients c`), plus `stage`, `status`, `role_name`,
 *     `owner_id`, `updated_at` — the exact fields `renderProjectList` reads for
 *     the 3 stat cards, the stage badge, the owner avatar and the Updated column.
 *   - `owner_id` is a free-form user id string (e.g. 'recruiter1') and may be
 *     null for a freshly created manual project; the UI falls back to 'Unknown'.
 *   - `stage` is 1-5 in the legacy UI (badge: 1 Pre-Kickoff, 2 Kick-off,
 *     3 Generation, 4 & 5 Launched). The v2 schema is migration-012-tolerant
 *     (column allows up to 6) but the stat/badge helpers below mirror the
 *     legacy 1-5 semantics verbatim.
 *
 * IDOR (carried over unchanged from `_common.md` gotcha): the legacy SPA SENT
 * `userId`/`role` in the body, but the executed `DB: Get Projects` node read
 * the identity from the validated session (`DB: Validate Session1`), NOT the
 * body. v2 enforces this — the route derives identity from `request.user`
 * (the session) and IGNORES any userId/role in query or body.
 *
 * Lives in its OWN file (not re-exported from the shared barrel by this agent);
 * the serial Wire pass adds the `export *` line to `packages/shared/src/index.ts`.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Stage semantics (dashboard.js renderProjectList — verbatim)
// ---------------------------------------------------------------------------
/**
 * A dashboard stat-card / filter bucket. Mirrors the three legacy stat cards
 * and `app._dashboardFilter` values exactly:
 *   - kickoff    → stage === 1                  (Awaiting Kick-off)
 *   - inprogress → stage 2..3                   (In Progress)
 *   - launched   → stage 4 & 5 — exactly the rows whose badge reads "Launched"
 *                  (E12-F17-T13; the legacy `stage===5 || status==='launched'`
 *                  rule missed stage-4 rows and showed LAUNCHED 0)
 */
export const DASHBOARD_FILTERS = ['kickoff', 'inprogress', 'launched'];
/** Legacy stage badge label per stage number (renderProjectList `switch`). */
export const STAGE_LABELS = {
    1: 'Pre-Kickoff',
    2: 'Kick-off',
    3: 'Generation',
    4: 'Launched',
    5: 'Launched',
};
/** Legacy stage badge Tailwind class per stage number (renderProjectList `switch`). */
export const STAGE_BADGE_CLASSES = {
    1: 'bg-orange-100 text-orange-600',
    2: 'bg-blue-100 text-blue-600',
    3: 'bg-indigo-100 text-indigo-600',
    4: 'bg-emerald-100 text-emerald-700',
    5: 'bg-emerald-100 text-emerald-700',
};
// ---------------------------------------------------------------------------
// Read DTO — DB: Get Projects projection (SELECT p.*, c.name AS client_name)
// ---------------------------------------------------------------------------
/**
 * One project row for the Dashboard table, mirroring the legacy
 * `DB: Get Projects` SELECT (`SELECT p.*, c.name as client_name FROM projects p
 * LEFT JOIN clients c ON c.id = p.client_id … ORDER BY p.updated_at DESC`).
 *
 * Only the fields the Dashboard reads are pinned in the contract; `p.*` carries
 * more columns in the legacy payload, but the v2 list endpoint returns this
 * stable projection so the table/stats can never silently lose a field.
 */
export const projectListItemSchema = z.object({
    /** projects.id (serial). */
    id: z.number().int(),
    /** projects.role_name — the position title shown under the client name. */
    role_name: z.string().nullable(),
    /** projects.client_id (nullable — a manual project has no client yet). */
    client_id: z.number().int().nullable(),
    /** clients.name via LEFT JOIN — the bold "Client" cell; null until a client is linked. */
    client_name: z.string().nullable(),
    /** projects.contact_id (nullable — manual projects have none). */
    contact_id: z.number().int().nullable(),
    /** projects.owner_id — free-form user id string ('recruiter1'); UI falls back to 'Unknown'. */
    owner_id: z.string().nullable(),
    /** projects.stage — 1..6 (legacy UI uses 1..5; stats/badges treat 4 & 5 as Launched). */
    stage: z.number().int().nullable(),
    /** projects.status — active | archived | finalized | on_hold | launched. */
    status: z.string().nullable(),
    /** projects.google_folder_id — Drive folder link target (kept for parity). */
    google_folder_id: z.string().nullable(),
    /** projects.created_at — ISO 8601. */
    created_at: z.string(),
    /** projects.updated_at — ISO 8601; the Updated column (toLocaleDateString). */
    updated_at: z.string(),
});
/**
 * GET /projects response — a BARE ARRAY of project rows, newest-updated first
 * (`ORDER BY p.updated_at DESC`). The wire MUST stay an array (the legacy
 * `dashboard.js` branches on `Array.isArray(response)`); an empty portfolio is
 * `[]`, which the UI renders as the "No projects" empty state.
 */
export const projectListResponseSchema = z.array(projectListItemSchema);
// ---------------------------------------------------------------------------
// create_manual reply (consumed by the "New Project" button, T06)
// ---------------------------------------------------------------------------
/**
 * The shape the Dashboard "New Project" button expects from the
 * project-mutations create endpoint (`create_manual`, owned by E02-F03).
 * `dashboard.js → createManualProject` treats the call as a success ONLY when
 * `data.success && data.project_id` are both present, then opens the new
 * project (`openProject(data.project_id)`). On any other shape it shows
 * `data.error || 'Error creating project'`. This permissive schema captures
 * exactly those fields the button reads.
 */
export const createManualProjectResponseSchema = z.object({
    success: z.boolean().optional(),
    project_id: z.number().int().optional(),
    error: z.string().optional(),
});
//# sourceMappingURL=projects-dashboard.js.map